/**
 * German message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: informal du (locked in l10n/locales.md).
 *
 * Platform terms follow German UI conventions (Design for theme,
 * Unterhaltung for conversation, koppeln for pair, verwerfen for
 * dismiss); "Glance" is the untranslated feature brand name
 * (glossary D1) and "Clip"/"Clips" is a locked loanword (D2).
 *
 * Plural notes: German is a {one, other} CLDR locale. Nouns that are
 * invariant across both forms (Treffer, Zeichen) use a plain template;
 * everything else selects through `selectPlural("de", …)`.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Verbunden",
    statusReconnecting: "Verbindung wird wiederhergestellt…",
    statusDisconnected: "Getrennt",
    openSettings: "Einstellungen öffnen",
    pairedFallbackDevice: "dein Telefon",
    publicModeToggleEnable: "Benachrichtigungsinhalte ausblenden",
    publicModeToggleDisable: "Benachrichtigungsinhalte anzeigen",
  },

  notifications: {
    replyAriaLabel: "Antworten",
    replyPlaceholder: "Antworten…",
    replySend: "Senden",
    sent: "Gesendet",
    failedToSend: "Senden fehlgeschlagen",
    unread: "Ungelesen",
    dismiss: "Verwerfen",
    notificationImageAlt: "Benachrichtigungsbild",
  },

  conversations: {
    replyAriaLabel: "Antworten",
    replyPlaceholder: "Antworten…",
    replySend: "Senden",
    sent: "Gesendet",
    failedToSend: "Senden fehlgeschlagen",
    dismiss: "Unterhaltung verwerfen",
    messagesListLabel: "Nachrichten in dieser Unterhaltung",
    deviceOwner: "Du",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Keine ungelesenen Nachrichten"
        : `${count} ${selectPlural("de", count, {
            one: "ungelesene Nachricht",
            other: "ungelesene Nachrichten",
          })}`,
  },

  list: {
    today: "Heute",
    yesterday: "Gestern",
    searchNotifications: "Benachrichtigungen durchsuchen",
    clearSearch: "Suche löschen",
    clearAll: "Alle verwerfen",
    markAllRead: "Alle als gelesen markieren",
    groupCheckboxLabel: "Gruppieren",
    noNotificationsTitle: "Noch keine Benachrichtigungen",
    noNotificationsDesc: "Neue von deinem Telefon gespiegelte Benachrichtigungen erscheinen hier.",
    noMatchesTitle: "Keine Treffer",
    notificationsList: "Benachrichtigungen",
    privacyAppList: "Benachrichtigungen nach App",
    clear: "Verwerfen",
    // "Treffer" is invariant across German plural forms — plain template.
    clearMatches: (count: number): string => `${count} Treffer verwerfen`,
    noMatchesDesc: (query: string): string => `Keine Benachrichtigungen entsprechen „${query}“.`,
    appNotificationsGroup: (appName: string): string => `Benachrichtigungen von ${appName}`,
    dismissAllFrom: (appName: string): string => `Alle von ${appName} verwerfen`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("de", count, { one: "Nachricht", other: "Nachrichten" })
          : kind === "calls"
            ? selectPlural("de", count, { one: "Anruf", other: "Anrufe" })
            : selectPlural("de", count, { one: "Benachrichtigung", other: "Benachrichtigungen" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "verwerfen",
    kbdNavigate: "navigieren",
    kbdOrSeparator: "oder",
  },

  settings: {
    back: "Zurück zu den Benachrichtigungen",
    settings: "Einstellungen",
    appearance: "Darstellung",
    theme: "Design",
    themeAuto: "Automatisch",
    themeLight: "Hell",
    themeDark: "Dunkel",
    language: "Sprache",
    languageSystemDefault: "Systemstandard",
    notificationsSection: "Benachrichtigungen",
    osNotifications: "Systembenachrichtigungen",
    callNotifications: "Anrufbenachrichtigungen",
    privacySection: "Datenschutz",
    analyticsToggle: "Anonyme Nutzungsstatistiken",
    analyticsToggleDescription: "Nur Zahlen und Meilensteine — nie deine Benachrichtigungen.",
    connection: "Verbindung",
    status: "Status",
    statusConnected: "Verbunden",
    statusReconnecting: "Verbindung wird wiederhergestellt…",
    statusDisconnected: "Getrennt",
    disconnectFromPhone: "Vom Telefon trennen",
    disconnectConfirm: "Bist du sicher? Dadurch wird dein Gerät entkoppelt.",
    yesDisconnect: "Ja, trennen",
    cancel: "Abbrechen",
    aboutAndSupport: "Info & Support",
    version: "Version",
    encryption: "Verschlüsselung",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Brauchst du Hilfe? Schreib an __EMAIL__",
    disconnectFailed: "Trennen fehlgeschlagen — bitte versuche es erneut",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Getrennt, aber die lokale Bereinigung ist fehlgeschlagen: ${errorMsg}. Bitte lade die Seite neu.`,
    disconnectLocalCleanupFailed:
      "Getrennt, aber die lokale Bereinigung ist fehlgeschlagen. Bitte lade die Seite neu.",
  },

  pairing: {
    enterCodeInstruction: "Gib diesen Code in der Reflecto-App ein",
    pairedSuccessfully: "Erfolgreich gekoppelt!",
    pairingFailed: "Kopplung fehlgeschlagen",
    requestingPairingCode: "Kopplungscode wird angefordert…",
    codeExpired: "Code abgelaufen",
    tryAgain: "Erneut versuchen",
    failedToStartPairing: "Kopplung konnte nicht gestartet werden",
    needAppText: "Du brauchst die Android-App? Schreib an __EMAIL__, um Zugang zu erhalten.",
  },

  dnd: {
    title: "Nicht stören",
    description: "Pausiere Benachrichtigungen auf diesem Gerät für die nächste Stunde.",
    snooze1h: "1 Stunde pausieren",
    snoozeActiveUntil: (formattedTime: string): string => `Aktiv bis ${formattedTime}`,
    cancelSnooze: "Abbrechen",
  },

  connectors: {
    title: "Verbundene Dienste",
    emptyBody:
      "Noch keine verbundenen Dienste. Verbinde ChatGPT oder Claude über deren Connector-Einstellungen und bestätige dann den Code auf deinem Telefon.",
    scopeSend: "Kann Benachrichtigungen senden",
    added: (date: string): string => `Hinzugefügt ${date}`,
    lastUsed: (relative: string): string => `Zuletzt verwendet ${relative}`,
    neverUsed: "Nie verwendet",
    revoke: "Trennen",
    revokeConfirm: (label: string): string => `${label} trennen? Der Zugriff endet sofort.`,
    revokeAction: "Trennen",
    revokeConfirmLabel: (label: string): string => `${label} trennen`,
    cancel: "Abbrechen",
    loadError: "Verbundene Dienste konnten nicht geladen werden. Bitte versuche es erneut.",
    revokeError: "Der Dienst konnte nicht getrennt werden. Bitte versuche es erneut.",
    retry: "Wiederholen",
  },

  feedback: {
    title: "Feedback senden",
    close: "Schließen",
    messageLabel: "Deine Nachricht",
    messagePlaceholder:
      "Einen Bug gefunden, eine Idee, oder fühlt sich etwas falsch an? Erzähl es uns.",
    categoryLabel: "Kategorie",
    categoryBug: "Bug",
    categoryIdea: "Idee",
    categoryOther: "Sonstiges",
    emailLabel: "E-Mail (optional, nur falls du eine Antwort möchtest)",
    emailPlaceholder: "name@beispiel.de",
    emailHelp: "Wir setzen dich auf keine Liste.",
    disclosure:
      "Deine Nachricht wird an den Reflecto-Server gesendet und in unserem internen Issue-Tracker gespeichert. Wir hängen deine App-Version und eine bei der Kopplung vergebene Geräte-ID an, um Berichte zuordnen zu können. Wir sehen weder deine Benachrichtigungen noch Kontakte oder andere persönliche Daten. Die E-Mail-Adresse ist optional — gib sie nur an, wenn du eine Antwort möchtest. Wir setzen dich auf keine Liste.",
    submit: "Feedback senden",
    sending: "Wird gesendet…",
    success: "Danke — wird von einem Menschen gelesen, meist innerhalb von 24 h",
    error: "Senden fehlgeschlagen. Bitte versuche es erneut.",
    done: "Fertig",
    sendFeedback: "Feedback senden",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Schließen",
    aiSummaryTag: "KI-Zusammenfassung",
    errorAi: "Zusammenfassung konnte gerade nicht erstellt werden.",
    retryCta: "Erneut versuchen",
    downloadHeading: "KI-Zusammenfassungen erhalten",
    downloadBody:
      "Verschaffe dir mit einer KI-Zusammenfassung direkt auf dem Gerät einen Überblick über Verpasstes. Läuft vollständig auf deinem Gerät.",
    downloadCta: "Modell herunterladen",
    downloadPrivacyNote: "Einmaliger Download. Der Server sieht deine Benachrichtigungen nie.",
    downloadingHeading: "Modell wird heruntergeladen…",
    caughtUp: "Alles erledigt — gerade braucht dich nichts.",
    needsYou: "Braucht dich",
    quiet: "Ruhig",
    scanning: (pending: number): string => `Fasse ${pending} zusammen…`,
    jump: "Benachrichtigung öffnen",
    affordanceReply: "Antworten",
    affordanceOpen: "Öffnen",
    affordanceCopyOtp: "Code kopieren",
    buttonLabel: "Glance",
  },

  update: {
    title: "Update verfügbar",
    description: "Eine neue Version von Reflecto ist bereit. Lade neu, um zu aktualisieren.",
    reload: "Neu laden",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Wir haben dieses Gerät „${name}“ genannt.`,
    cardLooksGood: "Passt",
    cardEdit: "Bearbeiten",
    cardAriaLabel: "Gerätenamen bestätigen",
    renameTitle: "Dieses Gerät umbenennen",
    renameLabel: "Gerätename",
    renameSave: "Speichern",
    renameCancel: "Abbrechen",
    renameFailed: "Umbenennen fehlgeschlagen. Bitte versuche es erneut.",
  },

  collision: {
    title: (name: string): string => `Du hast bereits ein „${name}“`,
    subtitle: "Wie sollen wir dieses hier nennen?",
    chipWork: (name: string): string => `Arbeit ${name}`,
    chipHome: (name: string): string => `Zuhause ${name}`,
    chipPersonal: (name: string): string => `Privat ${name}`,
    customLabel: "Eigener Name",
    save: "Speichern",
    useAnyway: "Trotzdem den ursprünglichen Namen verwenden",
    saveFailed: "Neuer Name konnte nicht gespeichert werden. Bitte versuche es erneut.",
    ariaLabel: "Doppelten Gerätenamen auflösen",
  },

  clipboard: {
    title: "Zwischenablage",
    empty: "Clips von deinen anderen Geräten erscheinen hier. Zum Kopieren tippen.",
    from: (label: string): string => `von ${label}`,
    copy: "In die Zwischenablage kopieren",
    copied: "Kopiert",
    reveal: "Anzeigen",
    hide: "Verbergen",
    maskedHint: "Inhalt verborgen – zum Anzeigen auf „Anzeigen“ tippen",
    sensitiveChip: "Vertraulich",
    cloudChip: "über dein API-Token",
    sendPlaceholder: "Text zum Senden einfügen…",
    sendCta: "Senden",
    sendSensitiveToggle: "Als vertraulich markieren",
    sendSuccess: (n: number): string =>
      `An ${n} ${selectPlural("de", n, { one: "Gerät", other: "Geräte" })} gesendet.`,
    sendPartial: (n: number, total: number): string =>
      `${n}/${total} ${selectPlural("de", total, { one: "Gerät", other: "Geräten" })} erreicht.`,
    sendNoDevices: "Keine gekoppelten Geräte zum Senden.",
    sendFailed: "Senden fehlgeschlagen. Bitte versuche es erneut.",
    sendEmpty: "Nichts zu senden.",
    dockExpand: "Clip-Verlauf anzeigen",
    dockCollapse: "Einklappen",
    dockSendTitle: "An deine Geräte senden",
    // "Zeichen" is invariant across German plural forms — plain template.
    dockFromClipboard: (chars: number): string => `Aus der Zwischenablage · ${chars} Zeichen`,
    dockSend: (n: number): string => `An ${n} senden`,
    dockCancel: "Abbrechen",
    dockRecentHeading: "Zuletzt",
    dockEmptyClipboard: "Deine Zwischenablage ist leer.",
    shareTitle: "An deine Geräte senden",
    shareReview:
      "Prüfe, was du sendest. Es geht Ende-zu-Ende-verschlüsselt an deine gekoppelten Geräte.",
    shareSend: "Senden",
    shareCancel: "Abbrechen",
    shareNotPaired: "Kopple zuerst ein Gerät, um geteilten Text zu senden.",
    shareOpenApp: "Reflecto öffnen",
  },
  install: {
    title: "Reflecto installieren",
    body: "Füge es zu deinem Startbildschirm hinzu, um schneller darauf zuzugreifen.",
    cta: "Installieren",
    dismissAria: "Installationshinweis verwerfen",
  },
  common: {
    loading: "Wird geladen",
  },
  errors: {
    notPaired: "Nicht gekoppelt",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} auf ${os}`,
    webFallback: "Webbrowser",
  },
  palette: {
    placeholder: "Befehl eingeben…",
    noResults: "Keine passenden Befehle",
    ariaLabel: "Befehlspalette",
    clearAll: "Alle Benachrichtigungen verwerfen",
    glance: "Glance-Zusammenfassung",
    cancelSnooze: "Pause abbrechen",
    copyClip: "Letzten empfangenen Clip kopieren",
  },
} satisfies MessagesShape;
